import type { HomeworkSource } from "@edu/shared";
import type { SourceKind } from "./subjects.ts";

/**
 * Заказ заданий с учётом того, что источника может не быть вовсе (1.4
 * `docs/homework-scratch-design.md`).
 *
 * ⚠️ **Без источника домашка уходит, только когда источник ЯВНО не выбран**:
 * ветка заготовки открыта, а сама заготовка не выбрана. Выбранный источник, из
 * которого не вышло ни одного задания, это не «домашка из материала», а
 * отказ `homework_empty` (1.4 и приёмка 6): преподаватель отметил заготовку, и
 * молча выдать без неё значило бы отправить не то, что он собирал.
 *
 * Отсюда три исхода. `none` это выдача без источника. `order` уходит на
 * сервер, в том числе заготовка с ПУСТЫМ составом: сервер понимает
 * `elementIds: []` как «ни одного» и отвечает `homework_empty`, а форма
 * показывает его отказ. `blocked` держит «Задать», пока ответить нечем:
 * заготовка грузится или не загрузилась, темы не готовы.
 */
export type SourceOrder =
  | { kind: "none" }
  | { kind: "order"; order: HomeworkSource }
  | { kind: "blocked"; reason: "plan_loading" | "plan_failed" | "topics" };

export function sourceOrder(args: {
  source: SourceKind;
  planId: string;
  planLoaded: boolean;
  planFailed: boolean;
  /** Отмеченные задания заготовки с верным ответом. */
  pickedIds: readonly string[];
  /** Заказ по темам, если ветка тем готова; иначе `null`. */
  topicOrder: HomeworkSource | null;
}): SourceOrder {
  if (args.source !== "plan") {
    return args.topicOrder === null
      ? { kind: "blocked", reason: "topics" }
      : { kind: "order", order: args.topicOrder };
  }
  if (args.planId === "") return { kind: "none" };
  if (args.planFailed) return { kind: "blocked", reason: "plan_failed" };
  if (!args.planLoaded) return { kind: "blocked", reason: "plan_loading" };
  return {
    kind: "order",
    order: { kind: "plan", planId: args.planId, elementIds: [...args.pickedIds] },
  };
}

/** Почему «Задать» недоступна со стороны заданий. */
export function sourceBlockText(reason: "plan_loading" | "plan_failed" | "topics"): string {
  switch (reason) {
    case "plan_loading":
      return "Заготовка ещё загружается.";
    case "plan_failed":
      return "Заготовка не загрузилась. Выберите её снова или уберите выбор заготовки.";
    case "topics":
      return "Допишите темы или переключитесь на заготовку.";
  }
}
